import { useNavigate } from "react-router-dom";
import { Bot, ArrowRight } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { strengthPct } from "@/pages/candidate/profile/parts";
import { cn } from "@/lib/utils";
import { FitRing, Panel } from "./parts";
import { DAY, fitTone } from "./portal";

const HINTS = [
  [(p) => p.headline, "Add a headline"],
  [(p) => p.summary, "Write a short summary"],
  [(p) => (p.skills || []).length >= 4, "Add a few more skills"],
  [(p) => (p.experience || []).length, "Add your experience"],
  [(p) => (p.projects || []).length, "Add a project"],
  [(p) => (p.certifications || []).length, "Add a certification"],
  [(p) => (p.education || []).length, "Add your education"],
  [(p) => (p.languages || []).length, "Add the languages you speak"],
];

export function ProfileStrengthCard({ profile }) {
  const nav = useNavigate();
  const pct = strengthPct(profile);
  const hint = profile ? HINTS.find(([ok]) => !ok(profile))?.[1] : null;
  return (
    <section className="hs-card p-5 flex items-center gap-4" data-testid="home-profile-strength">
      <FitRing value={pct} size={72} stroke={6} suffix="%" />
      <div className="min-w-0 leading-snug">
        <h2 className="text-[15px] font-semibold">Profile strength</h2>
        <p className="text-sm text-muted-foreground">{hint ? `${hint} to raise it` : pct < 100 ? "Fill in the remaining sections to raise it" : "Your profile is complete"}</p>
        <button onClick={() => nav("/candidate/profile")} className="mt-1 text-sm text-primary hover:underline inline-flex items-center gap-1" data-testid="home-profile-improve">
          {pct < 100 ? "Improve profile" : "View profile"} <ArrowRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </section>
  );
}

export function NewMatchesCard({ opp }) {
  const nav = useNavigate();
  const list = (opp?.opportunities || []).filter((o) => !o.applied && o.fit != null).slice(0, 3);
  const strong = (opp?.opportunities || []).filter((o) => !o.applied && o.fit >= 70).length;
  return (
    <Panel title="New matches" aside={opp && <span className="text-[13px] text-muted-foreground">{strong} strong</span>} testId="home-new-matches">
      {opp === undefined ? <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
        : list.length === 0 ? <p className="text-sm text-muted-foreground">No new matches right now — we'll surface roles as they open.</p> : (
          <ul className="divide-y divide-border -mx-1">
            {list.map((o) => (
              <li key={o.job.id}>
                <button onClick={() => nav(`/candidate/jobs?job=${o.job.id}`)} data-testid={`home-match-${o.job.id}`}
                  className="w-full flex items-center justify-between gap-3 px-1 py-3 text-left rounded-md hover:bg-foreground/[0.03] transition-colors">
                  <span className="min-w-0 leading-tight">
                    <span className="block text-sm font-semibold truncate">{o.job.title}</span>
                    <span className="block text-[13px] text-muted-foreground truncate">{[o.company_name, o.job.location].filter(Boolean).join(" · ")}</span>
                  </span>
                  <span className={cn("text-sm font-semibold tabular-nums shrink-0", fitTone(o.fit))}>{Math.round(o.fit)}%</span>
                </button>
              </li>
            ))}
          </ul>
        )}
    </Panel>
  );
}

export function AutoApplyCard({ auto, onToggle }) {
  const nav = useNavigate();
  if (!auto) return null;
  const applied = (auto.log || []).filter((e) => e.action === "applied").length;
  return (
    <section className="hs-card p-5 flex items-center gap-3.5" data-testid="home-auto-apply">
      <Bot className={cn("h-5 w-5 shrink-0", auto.enabled ? "text-primary" : "text-muted-foreground")} />
      <button onClick={() => nav("/candidate/auto-apply")} className="min-w-0 flex-1 text-left leading-tight" data-testid="home-auto-apply-open">
        <span className="block text-[15px] font-semibold">Auto-apply is {auto.enabled ? "on" : "off"}</span>
        <span className="block text-[13px] text-muted-foreground truncate">{auto.enabled ? `${applied} applied · ${auto.min_fit}%+ fit` : "Let the agent apply to strong matches"}</span>
      </button>
      <Switch checked={!!auto.enabled} onCheckedChange={onToggle} aria-label="Toggle auto-apply" data-testid="home-auto-apply-toggle" />
    </section>
  );
}

export function WeekCard({ apps, interviews, opps }) {
  const now = Date.now();
  const rows = [
    ["Applications sent", apps.filter((a) => now - Date.parse(a.applied_at) < 7 * DAY).length],
    ["Interviews coming up", (interviews || []).filter((i) => i.status !== "cancelled" && Date.parse(i.when) > now).length],
    ["Strong matches open", opps.filter((o) => !o.applied && o.fit >= 70).length],
  ];
  return (
    <Panel title="This week" testId="home-this-week">
      <dl className="space-y-2.5">
        {rows.map(([k, v]) => (
          <div key={k} className="flex items-center justify-between text-sm">
            <dt className="text-muted-foreground">{k}</dt>
            <dd className="font-semibold tabular-nums">{v}</dd>
          </div>
        ))}
      </dl>
    </Panel>
  );
}
